/**
 * NativeSelect — HTML `<select>` styled to match Shadcn's SelectTrigger.
 *
 * Why a native select instead of Radix UI Select in some places?
 *  • iOS Safari has a long-standing bug where Radix's portal-based Select
 *    renders blank when an ancestor has `transform`/`filter` (e.g. our
 *    animated `landing-ambient` halos on the onboarding screen). The portal
 *    coordinates get computed against the transformed ancestor and the
 *    dropdown ends up off-screen.
 *  • On mobile, native `<select>` triggers iOS's wheel picker / Android's
 *    bottom sheet, which are objectively better UX than a custom dropdown
 *    for short option lists.
 *  • On desktop, the native dropdown looks fine and behaves identically.
 *
 * API:
 *   <NativeSelect
 *     value={profile.currency}
 *     onValueChange={(v) => setProfile({ ...profile, currency: v })}
 *     options={[{ value: "USD", label: "USD" }, ...]}
 *     placeholder="Select currency"
 *     testid="onb-currency"
 *     className="h-11"
 *   />
 *
 * Note: kept intentionally simple — only what the onboarding flow uses.
 */
import * as React from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

export const NativeSelect = React.forwardRef(
  ({ value, onValueChange, options = [], placeholder, testid, className, disabled, ...rest }, ref) => {
    return (
      <div className={cn("relative", className)}>
        <select
          ref={ref}
          data-testid={testid}
          value={value ?? ""}
          disabled={disabled}
          onChange={(e) => onValueChange?.(e.target.value)}
          // Match Shadcn SelectTrigger styling exactly so the form is visually
          // consistent regardless of which select implementation is used.
          // `appearance-none` strips the browser chrome on desktop; we draw
          // our own chevron. On iOS/Android the OS still opens its native
          // picker because it's a real <select>.
          className={cn(
            "appearance-none w-full rounded-md border border-input bg-white px-3 pr-9 py-2 text-sm shadow-sm",
            "focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-500",
            "disabled:cursor-not-allowed disabled:opacity-50",
            // Apply same height as siblings (h-11 etc) via className prop above.
            "h-full",
            // Placeholder color when value is empty
            !value && "text-slate-400",
          )}
          {...rest}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((opt) => (
            <option key={opt.value} value={opt.value} className="text-slate-900">
              {opt.label}
            </option>
          ))}
        </select>
        {/* Chevron rendered on top of the select; pointer-events-none so the
            native click still opens the picker. */}
        <ChevronDown
          aria-hidden
          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400"
        />
      </div>
    );
  },
);
NativeSelect.displayName = "NativeSelect";
